// ==================== Web Push ของ livechat ====================
// แจ้งเตือนบน browser เมื่อมีข้อความแชทใหม่ (แอดมิน / AI / ฝาก-ถอนสำเร็จ) แม้ปิดหน้าแชทอยู่
//
// ทุกเส้นเรียก backend-agent-player /customer/push/* ซึ่ง proxy ต่อไป gateway
// (gateway: docs/api/web-push-integration-guide.md) — เฉพาะสมาชิกที่ login แล้ว
//
// Service Worker ใช้ตัวเดิม /serviceWorker.js (register ใน public/index.html)
// 1 scope มี SW ได้ตัวเดียว ห้ามสร้าง sw.js แยก

import { apiServices } from "@core/services/appService";
import { getCustomerToken } from "@core/auth/jwt/jwtService";

const SW_PATH = "/serviceWorker.js";

// ต้องตรงกับ PUSH_META_CACHE / PUSH_META_KEY ใน public/serviceWorker.js
// เก็บ URL ไอคอนของเว็บ (มาจาก CMS) ให้ SW ใช้ตอนแสดงแจ้งเตือน — ไฟล์ใน public/ เป็นโลโก้ React
const PUSH_META_CACHE = "livechat-push-meta";
const PUSH_META_KEY = "/__livechat-push-meta";

const READ_THROTTLE_MS = 10000;
let lastReadAt = 0;
let pendingReadTimer = null;

export const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const IN_APP_BROWSER =
  typeof navigator !== "undefined" &&
  /Line\/|FBAN|FBAV|Instagram/i.test(navigator.userAgent || "");

// body เป็น object ซ้อน (subscription) จึงต้องส่ง JSON — ค่า default ของ apiServices เป็น form-urlencoded
const postPush = (path, body = {}) =>
  apiServices(body, "post", `/customer/push${path}`, "", "application/json");

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  return reg || navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

async function getSubscription() {
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  return reg ? reg.pushManager.getSubscription() : null;
}

// ส่ง URL ไอคอนของเว็บให้ SW (อ่านจาก <link> ที่ Layouts.js ตั้งจาก CMS)
async function savePushMeta() {
  try {
    const icon =
      document.querySelector("#apple-touch-project")?.href ||
      document.querySelector("#icon-rel")?.href ||
      "";
    const cache = await caches.open(PUSH_META_CACHE);
    await cache.put(
      PUSH_META_KEY,
      new Response(JSON.stringify({ icon }), {
        headers: { "Content-Type": "application/json" },
      }),
    );
  } catch (e) {
    // ไม่มีไอคอนก็ยังแจ้งเตือนได้
  }
}

async function sendSubscription(sub) {
  await savePushMeta();
  const res = await postPush("/subscribe", { subscription: sub.toJSON() });
  return res?.code === 0;
}

// สถานะสำหรับแถบเปิดการแจ้งเตือนในหน้าแชท
//   hidden     : ไม่ต้องแสดงอะไร (ไม่ login / browser ไม่รองรับ / back office ยังไม่เปิดใช้)
//   denied     : ผู้ใช้เคยกดบล็อก — ขอใหม่ไม่ได้ ต้องไปเปิดในการตั้งค่า browser
//   subscribed : เปิดอยู่แล้ว (ผูกกับบัญชีที่ login อยู่ให้ใหม่ทุกครั้ง)
//   available  : แสดงปุ่มเปิดการแจ้งเตือน
export async function getPushState() {
  if (!PUSH_SUPPORTED || !getCustomerToken()) return { state: "hidden" };
  // in-app browser (LINE / Facebook / Instagram) บางตัวมี PushManager แต่ใช้จริงไม่ได้ — ซ่อนไปเลย
  if (IN_APP_BROWSER) return { state: "hidden" };

  const res = await postPush("/config");
  const config = res?.code === 0 ? res.result : null;
  if (!config?.enabled || !config?.vapid_public_key) return { state: "hidden" };

  if (Notification.permission === "denied") return { state: "denied", config };

  if (Notification.permission === "granted") {
    const sub = await getSubscription();
    if (sub) {
      // เคยอนุญาตแล้ว — ผูก subscription กับบัญชีที่ login อยู่ (เรียกซ้ำได้ gateway ไม่สร้างซ้ำ)
      sendSubscription(sub).catch(() => {});
      return { state: "subscribed", config };
    }
  }

  return { state: "available", config };
}

// เรียกจากการกดปุ่มของผู้ใช้เท่านั้น — browser บล็อก permission prompt ที่ไม่ได้มาจาก user action
// คืนค่า: "subscribed" | "denied" | "failed"
export async function enablePush(config) {
  try {
    if (!PUSH_SUPPORTED || !config?.vapid_public_key) return "failed";

    const permission = await Notification.requestPermission();
    if (permission === "denied") return "denied";
    if (permission !== "granted") return "failed";

    const reg = await getRegistration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.vapid_public_key),
      });
    }

    return (await sendSubscription(sub)) ? "subscribed" : "failed";
  } catch (e) {
    return "failed";
  }
}

// เรียกหลัง login ทุกครั้ง: ถ้าเคยอนุญาตแล้ว ให้ผูก subscription กับบัญชีใหม่
export async function syncPushAfterLogin() {
  try {
    if (!PUSH_SUPPORTED || Notification.permission !== "granted") return;
    const sub = await getSubscription();
    if (sub) await sendSubscription(sub);
  } catch (e) {
    // best effort
  }
}

// แจ้ง gateway ว่าอ่านแชทแล้ว → หยุดส่งแจ้งเตือนซ้ำ (ไม่เกิน 1 ครั้ง/10 วินาที)
// ถ้าถูกเรียกระหว่างรอ จะยิงตามอีกครั้งตอนครบเวลา — ข้อความที่เข้ามาช่วงนั้นต้องนับว่าอ่านด้วย
export function markChatRead() {
  if (!getCustomerToken()) return;
  if (document.visibilityState !== "visible") return;

  const wait = READ_THROTTLE_MS - (Date.now() - lastReadAt);
  if (wait > 0) {
    if (!pendingReadTimer) {
      pendingReadTimer = setTimeout(() => {
        pendingReadTimer = null;
        markChatRead();
      }, wait);
    }
    return;
  }
  lastReadAt = Date.now();

  postPush("/read").catch(() => {});
}

// เรียก "ก่อน" ล้าง session ตอน logout — backend ต้องรู้ว่าเป็นสมาชิกคนไหน
// จำกัดเวลารอ 3 วินาที ไม่ให้การ logout ค้างถ้า API ช้า
export async function disablePushOnLogout() {
  try {
    if (!PUSH_SUPPORTED) return;
    const sub = await getSubscription();
    if (!sub) return;

    await Promise.race([
      postPush("/unsubscribe", { endpoint: sub.endpoint }),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    await sub.unsubscribe();
  } catch (e) {
    // best effort — ถ้าพลาด gateway จะย้ายแจ้งเตือนไปบัญชีที่ subscribe ล่าสุดบนเครื่องนี้เอง
  }
}
